/**
 * MessageDrawer Component
 * 
 * Slide-out drawer for quick access to support messages.
 * Shows thread list and allows creating new threads.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  X, 
  Send, 
  Plus, 
  MessageSquare, 
  Clock, 
  CheckCircle2, 
  Loader2,
  Paperclip,
  ExternalLink,
  AlertCircle,
  ChevronRight,
  Upload,
  FileText,
  Image as ImageIcon
} from 'lucide-react';

const CATEGORIES = [
  { value: 'general_support', label: 'General Support', color: 'bg-blue-100 text-blue-700' },
  { value: 'technical_issue', label: 'Technical Issue', color: 'bg-red-100 text-red-700' },
  { value: 'billing_query', label: 'Billing Query', color: 'bg-amber-100 text-amber-700' },
  { value: 'feature_request', label: 'Feature Request', color: 'bg-purple-100 text-purple-700' }
];

const MessageDrawer = ({ isOpen, onClose, organisationId, organisationName }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [showNewThread, setShowNewThread] = useState(false);
  
  // New thread form
  const [newThread, setNewThread] = useState({
    subject: '',
    category: 'general_support',
    message: ''
  });
  
  // Reply form
  const [replyMessage, setReplyMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Fetch threads
  useEffect(() => {
    if (isOpen && organisationId) {
      fetchThreads();
      
      // Set up realtime subscription for new messages
      const channel = supabase
        .channel('support_messages_changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'support_messages'
          },
          (payload) => {
            // Refresh messages if we're viewing the affected thread
            if (selectedThread?.id === payload.new.thread_id) {
              fetchMessages(selectedThread.id);
            }
            // Also refresh thread list to update last_message_at
            fetchThreads();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [isOpen, organisationId, selectedThread?.id]);

  const fetchThreads = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('support_threads')
        .select(`
          *,
          support_messages(id, is_read, sender_type, created_at)
        `)
        .eq('organisation_id', organisationId)
        .order('last_message_at', { ascending: false });

      if (error) throw error;
      
      // Calculate unread count for each thread
      const threadsWithUnread = (data || []).map(thread => ({
        ...thread,
        unread_count: thread.support_messages?.filter(m => m.sender_type === 'admin' && !m.is_read).length || 0
      }));
      
      setThreads(threadsWithUnread);
    } catch (error) {
      console.error('Error fetching threads:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (threadId) => {
    try {
      setLoadingMessages(true);
      const { data, error } = await supabase
        .from('support_messages')
        .select(`
          *,
          sender:users(id, name, email)
        `)
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
      
      // Mark admin messages as read
      const unreadAdminMsgs = (data || []).filter(m => m.sender_type === 'admin' && !m.is_read);
      if (unreadAdminMsgs.length > 0) {
        await supabase
          .from('support_messages')
          .update({ is_read: true })
          .in('id', unreadAdminMsgs.map(m => m.id));
        
        // Refresh thread list to update unread counts
        fetchThreads();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSelectThread = async (thread) => {
    setSelectedThread(thread);
    await fetchMessages(thread.id);
  };

  const handleCreateThread = async () => {
    if (!newThread.subject.trim() || !newThread.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setSending(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', user.id)
        .single();

      // Create thread
      const { data: thread, error: threadError } = await supabase
        .from('support_threads')
        .insert({
          organisation_id: organisationId,
          subject: newThread.subject,
          category: newThread.category,
          created_by: userData?.id
        })
        .select()
        .single();

      if (threadError) throw threadError;

      // Create first message
      const { error: messageError } = await supabase
        .from('support_messages')
        .insert({
          thread_id: thread.id,
          sender_id: userData?.id,
          sender_type: 'user',
          body: newThread.message,
          attachment_urls: attachments
        });

      if (messageError) throw messageError;

      toast.success('Message sent! We\'ll respond within 24 hours.');
      setNewThread({ subject: '', category: 'general_support', message: '' });
      setAttachments([]);
      setShowNewThread(false);
      await fetchThreads();
      handleSelectThread(thread);
    } catch (error) {
      console.error('Error creating thread:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() && attachments.length === 0) {
      toast.error('Please enter a message');
      return;
    }

    try {
      setSending(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', user.id)
        .single();

      const { error } = await supabase
        .from('support_messages')
        .insert({
          thread_id: selectedThread.id,
          sender_id: userData?.id,
          sender_type: 'user',
          body: replyMessage,
          attachment_urls: attachments
        });

      if (error) throw error;

      // Reopen thread if it was closed
      if (selectedThread.status === 'closed') {
        await supabase
          .from('support_threads')
          .update({ status: 'open' })
          .eq('id', selectedThread.id);
      }

      toast.success('Reply sent!');
      setReplyMessage('');
      setAttachments([]);
      await fetchMessages(selectedThread.id);
      await fetchThreads();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Only PDF, PNG, and JPG files are allowed`);
        continue;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: File size must be under 10MB`);
        continue;
      }

      try {
        setUploading(true);
        const fileExt = file.name.split('.').pop();
        const fileName = `${organisationId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('message-attachments')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('message-attachments')
          .getPublicUrl(fileName);

        setAttachments(prev => [...prev, {
          name: file.name,
          url: publicUrl,
          type: file.type
        }]);
        
        toast.success(`${file.name} uploaded`);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      } finally {
        setUploading(false);
      }
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getCategoryInfo = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[0];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-r from-[#003B5C] to-[#005580]">
          <div className="flex items-center space-x-3">
            <MessageSquare className="w-5 h-5 text-white" />
            <div>
              <h2 className="font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Message Centre
              </h2>
              <p className="text-xs text-white/70">{organisationName}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { onClose(); navigate('/messages'); }}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Full View
            </Button>
            <button onClick={onClose} className="text-white/70 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {selectedThread ? (
            /* Conversation View */
            <>
              {/* Thread Header */}
              <div className="p-3 border-b border-slate-200 bg-slate-50">
                <button 
                  onClick={() => setSelectedThread(null)}
                  className="text-sm text-[#003B5C] hover:underline flex items-center mb-2"
                >
                  ← Back to threads
                </button>
                <h3 className="font-medium text-slate-800">{selectedThread.subject}</h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryInfo(selectedThread.category).color}`}>
                    {getCategoryInfo(selectedThread.category).label}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${selectedThread.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                    {selectedThread.status === 'open' ? 'Open' : 'Closed'}
                  </span>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loadingMessages ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div 
                      key={msg.id}
                      className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%] rounded-lg p-3 ${
                        msg.sender_type === 'user' 
                          ? 'bg-[#003B5C] text-white' 
                          : 'bg-slate-100 text-slate-800'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                        
                        {/* Attachments */}
                        {msg.attachment_urls && msg.attachment_urls.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {msg.attachment_urls.map((att, idx) => (
                              <a
                                key={idx}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center text-xs ${msg.sender_type === 'user' ? 'text-white/80 hover:text-white' : 'text-blue-600 hover:underline'}`}
                              >
                                {att.type?.includes('image') ? <ImageIcon className="w-3 h-3 mr-1" /> : <FileText className="w-3 h-3 mr-1" />}
                                {att.name}
                              </a>
                            ))}
                          </div>
                        )}
                        
                        <p className={`text-xs mt-1 ${msg.sender_type === 'user' ? 'text-white/60' : 'text-slate-400'}`}>
                          {msg.sender_type === 'admin' ? 'Lucent Sky Support' : 'You'} • {formatDate(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Reply Input */}
              <div className="p-3 border-t border-slate-200 bg-white">
                {/* Attachments Preview */}
                {attachments.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {attachments.map((att, idx) => (
                      <div key={idx} className="flex items-center bg-slate-100 rounded px-2 py-1 text-xs">
                        <span className="truncate max-w-[150px]">{att.name}</span>
                        <button onClick={() => removeAttachment(idx)} className="ml-1 text-slate-400 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex items-end space-x-2">
                  <div className="flex-1">
                    <Textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply..."
                      className="resize-none text-sm"
                      rows={2}
                    />
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    multiple
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="shrink-0"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                  </Button>
                  <Button
                    onClick={handleSendReply}
                    disabled={sending || (!replyMessage.trim() && attachments.length === 0)}
                    className="bg-[#003B5C] hover:bg-[#002A42] shrink-0"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          ) : showNewThread ? (
            /* New Thread Form */
            <div className="flex-1 overflow-y-auto p-4">
              <button 
                onClick={() => setShowNewThread(false)}
                className="text-sm text-[#003B5C] hover:underline flex items-center mb-4"
              >
                ← Back to threads
              </button>
              
              <h3 className="font-medium text-slate-800 mb-4">New Message</h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-sm">Category</Label>
                  <Select
                    value={newThread.category}
                    onValueChange={(val) => setNewThread(prev => ({ ...prev, category: val }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm">Subject</Label>
                  <Input
                    value={newThread.subject}
                    onChange={(e) => setNewThread(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Brief summary of your enquiry"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm">Message</Label>
                  <Textarea
                    value={newThread.message}
                    onChange={(e) => setNewThread(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Describe your enquiry in detail..."
                    className="mt-1 resize-none"
                    rows={6}
                  />
                </div>

                {/* Attachments */}
                <div>
                  <Label className="text-sm">Attachments (optional)</Label>
                  <div className="mt-1">
                    {attachments.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-2">
                        {attachments.map((att, idx) => (
                          <div key={idx} className="flex items-center bg-slate-100 rounded px-2 py-1 text-xs">
                            <span className="truncate max-w-[150px]">{att.name}</span>
                            <button onClick={() => removeAttachment(idx)} className="ml-1 text-slate-400 hover:text-red-500">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      multiple
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload Files
                    </Button>
                    <p className="text-xs text-slate-400 mt-1">PDF, PNG, JPG up to 10MB each</p>
                  </div>
                </div>
                
                <Button
                  onClick={handleCreateThread}
                  disabled={sending || !newThread.subject.trim() || !newThread.message.trim()}
                  className="w-full bg-[#003B5C] hover:bg-[#002A42]"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Message
                </Button>
              </div>
            </div>
          ) : (
            /* Thread List */
            <div className="flex-1 overflow-y-auto">
              {/* New Message Button */}
              <div className="p-3 border-b border-slate-200">
                <Button
                  onClick={() => setShowNewThread(true)}
                  className="w-full bg-[#74A57F] hover:bg-[#5F8575]"
                  data-testid="new-message-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Message
                </Button>
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : threads.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No messages yet</p>
                  <p className="text-sm text-slate-400 mt-1">Start a conversation with our support team</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {threads.map((thread) => (
                    <button
                      key={thread.id}
                      onClick={() => handleSelectThread(thread)}
                      className="w-full p-4 text-left hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-medium text-slate-800 truncate">
                              {thread.subject}
                            </h4>
                            {thread.unread_count > 0 && (
                              <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                                {thread.unread_count}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryInfo(thread.category).color}`}>
                              {getCategoryInfo(thread.category).label}
                            </span>
                            {thread.status === 'closed' && (
                              <span className="text-xs text-slate-400 flex items-center">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Closed
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center text-slate-400 ml-2">
                          <Clock className="w-3 h-3 mr-1" />
                          <span className="text-xs">{formatDate(thread.last_message_at)}</span>
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MessageDrawer;
